/**
 * Full notification list — `GET auth/notifications/`, paginated, scoped to
 * the caller. This is the tenant's dedicated nav destination
 * (`/tenant/notifications`); landlord and agent get a compact version
 * embedded in `ProfilePage` instead, since neither has a separate
 * "Notifications" nav item to hang a full page off of.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import {
  deleteNotification,
  listNotifications,
  markAllAsRead,
  markAsRead,
} from '../../api/endpoints/notifications';

export function NotificationsPage(): React.JSX.Element {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['notifications', { page }],
    queryFn: () => listNotifications({ page }),
  });

  const markRead = useMutation({
    mutationFn: (id: number) => markAsRead(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteNotification(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const notifications = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;
  const unreadCount = notifications.filter((notification) => !notification.is_read).length;

  return (
    <section>
      <h2>Notifications</h2>

      <p>
        <button
          type="button"
          onClick={() => markAllRead.mutate()}
          disabled={markAllRead.isPending || unreadCount === 0}
        >
          Mark all as read
        </button>
      </p>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}
      {query.isSuccess && notifications.length === 0 && <p>No notifications.</p>}

      {query.isSuccess && notifications.length > 0 && (
        <ul>
          {notifications.map((notification) => (
            <li
              key={notification.id}
              className={notification.is_read ? undefined : 'notification--unread'}
            >
              <strong>{notification.title}</strong> ({notification.created_at}) —{' '}
              {notification.message}{' '}
              {notification.is_read ? (
                <em>Read</em>
              ) : (
                <button
                  type="button"
                  onClick={() => markRead.mutate(notification.id)}
                  disabled={markRead.isPending}
                >
                  Mark as read
                </button>
              )}{' '}
              <button
                type="button"
                className="button--danger"
                aria-label={`Delete notification: ${notification.title}`}
                onClick={() => remove.mutate(notification.id)}
                disabled={remove.isPending}
              >
                <Trash2 size={14} className="icon-inline" aria-hidden="true" />
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default NotificationsPage;
